/**
 * Route Planning Service - logika biznesowa planowania tras.
 * Wykorzystuje turf.js do obliczeń geo (kontrahenci "po drodze").
 */

import nearestPointOnLine from '@turf/nearest-point-on-line';
import distance from '@turf/distance';
import { lineString, point } from '@turf/helpers';
// decoder z utils (nie composables/usePartnerContext) — ten moduł ma zostać
// czysty (testy jednostkowe), bez zależności od axios/Pinia
import { decodeXmlEntities } from '../utils/xml-fragment';
import type { Coordinate } from './osrm.service';

export interface GeoPartner {
  numrecpartner: string;
  shortName: string;
  fullName: string;
  city: string;
  address: string;
  phone: string;
  lat: number;
  lon: number;
  geoStatus: number;
  tlx: string;
}

export interface SalesSummary {
  totalNetto: number;
  lastSaleDate: string | null;
  invoiceCount: number;
}

export interface EnrichedPartner extends GeoPartner {
  distanceFromRoute: number;   // km from nearest point on route
  distanceFromStart: number;   // km straight-line from start point
  positionOnRoute: number;     // 0-1, position along route (for sorting)
  lastSaleDate: string | null;
  lastSaleAmount: number;
  hasOpenAffair: boolean;
  daysSinceLastSale: number | null;
  selected: boolean;
}

const ROUTE_BASE_LOCATION_KEY = 'route_base_location';

export interface BaseLocation {
  address: string;
  lat: number;
  lon: number;
}

export function getBaseLocation(): BaseLocation | null {
  try {
    const stored = localStorage.getItem(ROUTE_BASE_LOCATION_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (parsed.lat && parsed.lon && parsed.address) return parsed;
    return null;
  } catch {
    return null;
  }
}

export function saveBaseLocation(location: BaseLocation): void {
  localStorage.setItem(ROUTE_BASE_LOCATION_KEY, JSON.stringify(location));
}

/**
 * Simplify route geometry by sampling every Nth point.
 * OSRM overview=full returns thousands of points (~every 30m).
 * For "along the route" detection we sample ~every 1km to avoid
 * false positives from partners merely near a passing road.
 */
function simplifyRoute(geometry: Coordinate[], targetSpacingKm: number = 1): Coordinate[] {
  if (geometry.length <= 2) return geometry;

  const simplified: Coordinate[] = [geometry[0]];
  let lastAdded = geometry[0];

  for (let i = 1; i < geometry.length - 1; i++) {
    const d = distance(
      point([lastAdded.lon, lastAdded.lat]),
      point([geometry[i].lon, geometry[i].lat]),
      { units: 'kilometers' }
    );
    if (d >= targetSpacingKm) {
      simplified.push(geometry[i]);
      lastAdded = geometry[i];
    }
  }

  simplified.push(geometry[geometry.length - 1]);
  return simplified;
}

/**
 * Find partners within `radiusKm` of the route geometry,
 * excluding those within `startExclusionKm` of the start point.
 * Returns partners sorted from destination towards start (closest to destination first).
 */
export function findPartnersAlongRoute(
  routeGeometry: Coordinate[],
  partners: GeoPartner[],
  radiusKm: number,
  startPoint: Coordinate,
  startExclusionKm: number = 10
): EnrichedPartner[] {
  if (routeGeometry.length < 2 || partners.length === 0) return [];

  const simplified = simplifyRoute(routeGeometry);
  const line = lineString(simplified.map(c => [c.lon, c.lat]));
  const totalLength = simplified.length;
  const startPt = point([startPoint.lon, startPoint.lat]);

  console.log(`[RoutePlanning] Route: ${routeGeometry.length} pts → simplified to ${simplified.length} pts, exclusion zone: ${startExclusionKm} km`);

  const results: EnrichedPartner[] = [];

  for (const partner of partners) {
    if (partner.geoStatus <= 0) continue;

    const pt = point([partner.lon, partner.lat]);

    const distFromStart = distance(startPt, pt, { units: 'kilometers' });
    if (distFromStart <= startExclusionKm) continue;

    const nearest = nearestPointOnLine(line, pt, { units: 'kilometers' });
    const distFromRoute = distance(pt, nearest, { units: 'kilometers' });

    if (distFromRoute <= radiusKm) {
      const idx = nearest.properties.index ?? 0;
      const positionOnRoute = idx / Math.max(totalLength - 1, 1);

      results.push({
        ...partner,
        distanceFromRoute: Math.round(distFromRoute * 100) / 100,
        distanceFromStart: Math.round(distFromStart * 10) / 10,
        positionOnRoute,
        lastSaleDate: null,
        lastSaleAmount: 0,
        hasOpenAffair: false,
        daysSinceLastSale: null,
        selected: false,
      });
    }
  }

  // Sort from destination to start (highest positionOnRoute first)
  results.sort((a, b) => b.positionOnRoute - a.positionOnRoute);
  return results;
}

/**
 * Enrich partners with sales data and affair status.
 */
export function enrichPartnerData(
  partners: EnrichedPartner[],
  salesByPartner: Map<string, SalesSummary>,
  partnersWithOpenAffairs: Set<string>
): void {
  const now = new Date();

  for (const partner of partners) {
    const sales = salesByPartner.get(partner.numrecpartner);
    if (sales) {
      partner.lastSaleDate = sales.lastSaleDate;
      partner.lastSaleAmount = sales.totalNetto;

      if (sales.lastSaleDate) {
        const parts = sales.lastSaleDate.includes('-')
          ? sales.lastSaleDate.split('-')
          : sales.lastSaleDate.split('/').reverse();
        if (parts.length >= 3) {
          const saleDate = new Date(
            parseInt(parts[0]),
            parseInt(parts[1]) - 1,
            parseInt(parts[2])
          );
          partner.daysSinceLastSale = Math.floor(
            (now.getTime() - saleDate.getTime()) / (1000 * 60 * 60 * 24)
          );
        }
      }
    }

    partner.hasOpenAffair = partnersWithOpenAffairs.has(partner.numrecpartner);
  }
}

/**
 * Determine row highlight color based on partner status.
 * green = has open affair (priority), red = no sale >90 days, null = normal
 */
export function getPartnerHighlight(partner: EnrichedPartner): 'green' | 'red' | null {
  if (partner.hasOpenAffair) return 'green';
  if (partner.daysSinceLastSale !== null && partner.daysSinceLastSale > 90) return 'red';
  return null;
}

/**
 * Extract XML tag value (reused pattern from PartnerAnalysisPage).
 */
export function extractValue(xmlStr: string, tag: string): string {
  const regex = new RegExp(`<${tag}[^>]*>([^<]*)</${tag}>`, 'i');
  const match = xmlStr?.match(regex);
  return match ? decodeXmlEntities(match[1]) : '';
}

/**
 * Parse partners from proc 4005 response (XML lines), filtering to geocoded ones.
 */
export function parseGeoPartners(partnerLines: string[]): GeoPartner[] {
  const partners: GeoPartner[] = [];

  for (const xmlStr of partnerLines) {
    if (!xmlStr) continue;

    const geoStatus = parseInt(extractValue(xmlStr, 'geo_status')) || 0;
    const lat = parseFloat(extractValue(xmlStr, 'geo_lat').replace(',', '.'));
    const lon = parseFloat(extractValue(xmlStr, 'geo_lon').replace(',', '.'));

    if (geoStatus <= 0 || isNaN(lat) || isNaN(lon)) continue;

    partners.push({
      numrecpartner: extractValue(xmlStr, 'numrecpartner'),
      shortName: extractValue(xmlStr, 'partnershortname'),
      fullName: extractValue(xmlStr, 'partnerfulllname') || extractValue(xmlStr, 'partnerfullname'),
      city: extractValue(xmlStr, 'partnercity'),
      address: extractValue(xmlStr, 'partneradress'),
      phone: extractValue(xmlStr, 'partnerphone'),
      lat,
      lon,
      geoStatus,
      tlx: extractValue(xmlStr, 'tlx'),
    });
  }

  return partners;
}

/**
 * Parse sales data from proc 3012 response. Returns map of partner numrec -> sales summary.
 */
export function parseSalesData(salesResponse: any): Map<string, SalesSummary> {
  const map = new Map<string, SalesSummary>();
  if (!salesResponse?.data) return map;

  const data = salesResponse.data;
  const rows: { numrec: string; dateLine: string; netto: number; headChkIdent: string }[] = [];

  if (data.WorkLineChk && Array.isArray(data.WorkLineChk)) {
    for (const xmlStr of data.WorkLineChk) {
      if (!xmlStr || typeof xmlStr !== 'string') continue;
      const nettoStr = extractValue(xmlStr, 'LineChk_Value_Netto_InZloti');
      rows.push({
        numrec: extractValue(xmlStr, 'NumRecPartner'),
        dateLine: extractValue(xmlStr, 'DateLine'),
        netto: parseFloat(nettoStr.replace(',', '.')) || 0,
        headChkIdent: extractValue(xmlStr, 'HeadChkIdent'),
      });
    }
  } else if (data.NumRecPartner && Array.isArray(data.NumRecPartner)) {
    for (let i = 0; i < data.NumRecPartner.length; i++) {
      const nettoRaw = (data.LineChk_Value_Netto_InZloti || data.linechk_value_netto_inzloti)?.[i];
      rows.push({
        numrec: String(data.NumRecPartner[i] || ''),
        dateLine: String((data.DateLine || data.dateline)?.[i] || ''),
        netto: parseFloat(String(nettoRaw).replace(',', '.')) || 0,
        headChkIdent: String((data.HeadChkIdent || data.headchkident)?.[i] || ''),
      });
    }
  }

  for (const row of rows) {
    if (!row.numrec) continue;
    const existing = map.get(row.numrec);
    if (!existing) {
      map.set(row.numrec, {
        totalNetto: row.netto,
        lastSaleDate: row.dateLine || null,
        invoiceCount: 1,
      });
    } else {
      existing.totalNetto += row.netto;
      existing.invoiceCount++;
      if (row.dateLine && (!existing.lastSaleDate || row.dateLine > existing.lastSaleDate)) {
        existing.lastSaleDate = row.dateLine;
      }
    }
  }

  return map;
}

/**
 * Parse open affairs from proc 4000 response. Returns set of partner numrecs with open affairs.
 */
export function parseOpenAffairs(affairsResponse: any): Set<string> {
  const set = new Set<string>();
  if (!affairsResponse?.data) return set;

  const data = affairsResponse.data;
  const lines = data.linia;
  if (!lines) return set;

  const lineArray = Array.isArray(lines) ? lines : [lines];
  for (const xmlStr of lineArray) {
    if (!xmlStr || typeof xmlStr !== 'string') continue;
    const numrecPartner = extractValue(xmlStr, 'numrecpartner');
    if (numrecPartner && numrecPartner !== '0') {
      set.add(numrecPartner);
    }
  }

  return set;
}
